import React, { useState, useEffect } from 'react';
import {
  Truck,
  Shield,
  Download,
  Plus,
  RefreshCw,
  Search,
  CheckCircle,
  AlertTriangle,
  FileCheck,
  FileText,
  Upload,
  Globe,
  DollarSign,
  Percent,
  Layers,
  ArrowRight,
  ExternalLink,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  CommercialFleetQuoteRequestDto,
  CommercialFleetQuoteResultDto,
  BoundFleetPolicyDto,
  FleetVehicleScheduleItemDto,
  ComesaYellowCardCertificateDto
} from '../api/coreInsuranceApi';

// Country mapping for COMESA
const COMESA_COUNTRIES = [
  { id: 1, code: 'MW', name: 'Malawi' },
  { id: 2, code: 'ZM', name: 'Zambia' },
  { id: 3, code: 'MZ', name: 'Mozambique' },
  { id: 4, code: 'ZW', name: 'Zimbabwe' },
  { id: 5, code: 'TZ', name: 'Tanzania' },
  { id: 6, code: 'KE', name: 'Kenya' },
  { id: 7, code: 'UG', name: 'Uganda' },
  { id: 8, code: 'RW', name: 'Rwanda' },
  { id: 9, code: 'CD', name: 'DR Congo' },
  { id: 10, code: 'BI', name: 'Burundi' }
];

const VEHICLE_CLASSES = [
  { id: 1, label: 'Light Commercial Pick-Up (1-3.5T)' },
  { id: 2, label: 'Heavy Commercial Truck (3.5-15T)' },
  { id: 3, label: 'Prime Mover & Trailer (Articulated 30T+)' },
  { id: 4, label: 'Passenger Coach / Minibus' },
  { id: 5, label: 'Fuel Tanker / Hazardous Goods' },
  { id: 6, label: 'Special Construction / Earthmover' }
];

const SAMPLE_12_VEHICLE_FLEET: FleetVehicleScheduleItemDto[] = [
  {
    registrationNumber: 'BZ 1001',
    make: 'Scania',
    model: 'R500 Highline',
    yearOfManufacture: 2021,
    chassisNumber: 'YS2R4X20005829101',
    vehicleClass: 3, // Prime Mover
    coverType: 1, // Comprehensive
    sumInsured: 45000000,
    capacityOrTonnage: '34 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: true,
    comesaCountries: [2, 3, 4, 5] // ZM, MZ, ZW, TZ
  },
  {
    registrationNumber: 'BZ 1002',
    make: 'Volvo',
    model: 'FH16 Globetrotter',
    yearOfManufacture: 2022,
    chassisNumber: 'YV2RT40A9NA819201',
    vehicleClass: 3,
    coverType: 1,
    sumInsured: 48000000,
    capacityOrTonnage: '38 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: true,
    comesaCountries: [2, 3, 9] // ZM, MZ, CD
  },
  {
    registrationNumber: 'BZ 1003',
    make: 'Scania',
    model: 'G460',
    yearOfManufacture: 2020,
    chassisNumber: 'YS2G6X40003920194',
    vehicleClass: 3,
    coverType: 1,
    sumInsured: 40000000,
    capacityOrTonnage: '32 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: true,
    comesaCountries: [2, 3] // ZM, MZ
  },
  {
    registrationNumber: 'BZ 1004',
    make: 'Mercedes-Benz',
    model: 'Actros 3344',
    yearOfManufacture: 2021,
    chassisNumber: 'WDB9340331L992144',
    vehicleClass: 3,
    coverType: 1,
    sumInsured: 42000000,
    capacityOrTonnage: '34 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: true,
    comesaCountries: [2, 4] // ZM, ZW
  },
  {
    registrationNumber: 'BZ 2001',
    make: 'Mercedes-Benz',
    model: 'Actros 2640 Fuel Tanker',
    yearOfManufacture: 2021,
    chassisNumber: 'WDB9340331L552199',
    vehicleClass: 5, // Fuel Tanker
    coverType: 1,
    sumInsured: 52000000,
    capacityOrTonnage: '45,000 Litres',
    hasTrackingSystem: true,
    hasComesaYellowCard: true,
    comesaCountries: [3] // MZ
  },
  {
    registrationNumber: 'BZ 2002',
    make: 'Volvo',
    model: 'FM440 Fuel Tanker',
    yearOfManufacture: 2020,
    chassisNumber: 'YV2FM40A7MA112233',
    vehicleClass: 5,
    coverType: 1,
    sumInsured: 50000000,
    capacityOrTonnage: '40,000 Litres',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 3001',
    make: 'Toyota',
    model: 'Coaster Deluxe',
    yearOfManufacture: 2022,
    chassisNumber: 'JTDBR30E800192847',
    vehicleClass: 4, // Coach
    coverType: 1,
    sumInsured: 25000000,
    capacityOrTonnage: '30 Passengers',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 3002',
    make: 'Toyota',
    model: 'Coaster Deluxe',
    yearOfManufacture: 2022,
    chassisNumber: 'JTDBR30E800192848',
    vehicleClass: 4,
    coverType: 1,
    sumInsured: 25000000,
    capacityOrTonnage: '30 Passengers',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 4001',
    make: 'Isuzu',
    model: 'FVR 900 Rigid Truck',
    yearOfManufacture: 2021,
    chassisNumber: 'JALFVR900M7001923',
    vehicleClass: 2, // Heavy Commercial
    coverType: 1,
    sumInsured: 28000000,
    capacityOrTonnage: '15 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 4002',
    make: 'Isuzu',
    model: 'NPR 400 Delivery Truck',
    yearOfManufacture: 2023,
    chassisNumber: 'JALNPR400P7003829',
    vehicleClass: 2,
    coverType: 1,
    sumInsured: 22000000,
    capacityOrTonnage: '8 Tonnes',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 5001',
    make: 'Toyota',
    model: 'Hilux Double Cab 4x4',
    yearOfManufacture: 2023,
    chassisNumber: 'AHTFR22G909192837',
    vehicleClass: 1, // Light Commercial
    coverType: 1,
    sumInsured: 18000000,
    capacityOrTonnage: '1 Tonne',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  },
  {
    registrationNumber: 'BZ 5002',
    make: 'Toyota',
    model: 'Hilux Double Cab 4x4',
    yearOfManufacture: 2023,
    chassisNumber: 'AHTFR22G909192838',
    vehicleClass: 1,
    coverType: 1,
    sumInsured: 18000000,
    capacityOrTonnage: '1 Tonne',
    hasTrackingSystem: true,
    hasComesaYellowCard: false
  }
];

export default function CommercialFleets() {
  const [activeTab, setActiveTab] = useState<'quote' | 'directory' | 'comesa' | 'telematics'>('quote');
  const [boundFleets, setBoundFleets] = useState<BoundFleetPolicyDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [telematicsReport, setTelematicsReport] = useState<any>(null);
  const [loadingTelematics, setLoadingTelematics] = useState(false);

  // Quote Form State
  const [fleetName, setFleetName] = useState('Trans-Africa Logistics & Haulage Ltd');
  const [contactPerson, setContactPerson] = useState('Tiwonge Banda (Fleet Director)');
  const [contactPhone, setContactPhone] = useState('+265 888 123 456');
  const [contactEmail, setContactEmail] = useState('fleet@transafrica.mw');
  const [physicalAddress, setPhysicalAddress] = useState('Kanengo Industrial Area, Lilongwe, Malawi');
  const [lossRatio, setLossRatio] = useState<number>(28.5);
  const [vehicles, setVehicles] = useState<FleetVehicleScheduleItemDto[]>(SAMPLE_12_VEHICLE_FLEET);

  // Importer state
  const [csvText, setCsvText] = useState('');
  const [showCsvModal, setShowCsvModal] = useState(false);

  // Rating Result State
  const [calculating, setCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState<CommercialFleetQuoteResultDto | null>(null);

  // Binding State
  const [binding, setBinding] = useState(false);
  const [showBindModal, setShowBindModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('BankTransfer');
  const [paymentRef, setPaymentRef] = useState('EFT-STANDARD-BANK-FLT-2026-99120');
  const [boundResult, setBoundResult] = useState<BoundFleetPolicyDto | null>(null);

  // Load existing fleets on mount
  useEffect(() => {
    fetchBoundFleets();
  }, []);

  const fetchBoundFleets = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getBoundFleets();
      setBoundFleets(data);
    } catch {
      // Non-blocking in dev if list empty
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateQuote = async () => {
    if (!fleetName.trim()) {
      toast.error('Please enter the Commercial Fleet or Company name');
      return;
    }
    if (vehicles.length === 0) {
      toast.error('Please add at least one vehicle to the fleet schedule');
      return;
    }

    setCalculating(true);
    try {
      const request: CommercialFleetQuoteRequestDto = {
        fleetName,
        contactPerson,
        contactPhone,
        contactEmail,
        physicalAddress,
        past3YearsLossRatioPercent: lossRatio,
        vehicles
      };

      const result = await coreInsuranceApi.calculateFleetQuote(request);
      setQuoteResult(result);
      toast.success(`Quotation ${result.quoteNumber} calculated with bulk discount!`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to calculate commercial fleet quotation');
    } finally {
      setCalculating(false);
    }
  };

  const handleBindFleet = async () => {
    if (!quoteResult) return;
    setBinding(true);
    try {
      const bound = await coreInsuranceApi.bindFleetPolicy({
        quote: quoteResult,
        paymentMethod,
        paymentReference: paymentRef,
        amountPaid: quoteResult.totalGrossWrittenPremium
      });
      setBoundResult(bound);
      setShowBindModal(false);
      toast.success(`Fleet Policy ${bound.policyNumber} bound successfully!`);
      fetchBoundFleets();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to bind fleet policy');
    } finally {
      setBinding(false);
    }
  };

  const handleCsvImport = () => {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) {
        toast.error('CSV must contain a header line and at least one vehicle line.');
        return;
      }

      const parsed: FleetVehicleScheduleItemDto[] = [];
      // Format: Reg, Make, Model, Year, Chassis, Class(1-6), SumInsured, Tonnage, Tracking(true/false), Comesa(true/false)
      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',').map(s => s.trim());
        if (parts.length >= 7) {
          parsed.push({
            registrationNumber: parts[0] || `BZ ${1000 + i}`,
            make: parts[1] || 'Toyota',
            model: parts[2] || 'Truck',
            yearOfManufacture: parseInt(parts[3]) || 2021,
            chassisNumber: parts[4] || `CHAS-${10000 + i}`,
            vehicleClass: parseInt(parts[5]) || 2,
            coverType: 1,
            sumInsured: parseFloat(parts[6]) || 25000000,
            capacityOrTonnage: parts[7] || '10 Tonnes',
            hasTrackingSystem: parts[8]?.toLowerCase() === 'true',
            hasComesaYellowCard: parts[9]?.toLowerCase() === 'true',
            comesaCountries: parts[9]?.toLowerCase() === 'true' ? [2, 3] : []
          });
        }
      }

      if (parsed.length > 0) {
        setVehicles(parsed);
        setShowCsvModal(false);
        setCsvText('');
        toast.success(`Imported ${parsed.length} vehicles into schedule!`);
      } else {
        toast.error('Could not parse any valid vehicles from the CSV.');
      }
    } catch {
      toast.error('Error parsing CSV. Please check formatting.');
    }
  };

  const handleAddBlankVehicle = () => {
    const newIdx = vehicles.length + 1;
    setVehicles([
      ...vehicles,
      {
        registrationNumber: `BZ ${3000 + newIdx}`,
        make: 'Isuzu',
        model: 'FVR Truck',
        yearOfManufacture: 2022,
        chassisNumber: `JAL-${Date.now().toString().slice(-8)}`,
        vehicleClass: 2,
        coverType: 1,
        sumInsured: 25000000,
        capacityOrTonnage: '10 Tonnes',
        hasTrackingSystem: true,
        hasComesaYellowCard: false,
        comesaCountries: []
      }
    ]);
  };

  const handleRemoveVehicle = (index: number) => {
    setVehicles(vehicles.filter((_, i) => i !== index));
  };

  const handleToggleComesa = (index: number) => {
    setVehicles(
      vehicles.map((v, i) =>
        i === index
          ? {
              ...v,
              hasComesaYellowCard: !v.hasComesaYellowCard,
              comesaCountries: !v.hasComesaYellowCard ? [2, 3] : []
            }
          : v
      )
    );
  };

  const handleCountryToggle = (vehicleIndex: number, countryId: number) => {
    setVehicles(
      vehicles.map((v, i) => {
        if (i !== vehicleIndex) return v;
        const current = v.comesaCountries || [];
        const next = current.includes(countryId)
          ? current.filter(c => c !== countryId)
          : [...current, countryId];
        return { ...v, comesaCountries: next };
      })
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Title & Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 rounded-2xl text-white shadow-xl border border-indigo-900/40">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
              <Globe className="w-3 h-3" /> COMESA Yellow Card Protocol
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Tiered Bulk Fleet Discounts
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Commercial Lines Underwriting & Fleet Management</h1>
          <p className="text-slate-300 text-sm mt-1">
            Bulk schedule rating, tonnage classification, COMESA Yellow Card Cross-Border Transit certification, and vector document issuance.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveTab('quote')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'quote'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Fleet Underwriter
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'directory'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Bound Fleets ({boundFleets.length})
          </button>
          <button
            onClick={() => setActiveTab('comesa')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'comesa'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            COMESA Hub
          </button>
          <button
            onClick={async () => {
              setActiveTab('telematics');
              if (!telematicsReport) {
                setLoadingTelematics(true);
                try {
                  const regs = vehicles.map(v => v.registrationNumber);
                  const data = await coreInsuranceApi.getFleetTelematicsAudit(fleetName, regs);
                  setTelematicsReport(data);
                } catch {
                  toast.error('Failed to load telematics report');
                } finally {
                  setLoadingTelematics(false);
                }
              }
            }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'telematics'
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            IoT Telematics & Risk Score
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
            <Truck className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Active Scheduled Units</div>
            <div className="text-2xl font-bold text-slate-900">
              {vehicles.length} <span className="text-xs font-normal text-slate-500">vehicles</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <Globe className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">COMESA Yellow Cards</div>
            <div className="text-2xl font-bold text-slate-900">
              {vehicles.filter(v => v.hasComesaYellowCard).length}{' '}
              <span className="text-xs font-normal text-slate-500">units certified</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <Percent className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Active Bulk Discount</div>
            <div className="text-2xl font-bold text-slate-900">
              {vehicles.length >= 26 ? '17.5%' : vehicles.length >= 11 ? '12.5%' : vehicles.length >= 5 ? '7.5%' : '0%'}
              {lossRatio < 30 && <span className="text-xs text-emerald-600 font-semibold ml-1">+2.5% safe</span>}
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs text-slate-500 uppercase font-semibold">Fleet Exposure (Sum Insured)</div>
            <div className="text-xl font-bold text-slate-900">
              MWK {vehicles.reduce((sum, v) => sum + (v.sumInsured || 0), 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* TAB 1: FLEET UNDERWRITER */}
      {activeTab === 'quote' && (
        <div className="space-y-6">
          {/* Fleet Policyholder Metadata Form */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 mb-4 border-b border-slate-100 gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">1. Commercial Account Particulars</h2>
                <p className="text-xs text-slate-500">Corporate policyholder credentials and underwriting loss history</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setVehicles(SAMPLE_12_VEHICLE_FLEET)}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Load Sample 12-Unit Fleet
                </button>
                <button
                  type="button"
                  onClick={() => setShowCsvModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors flex items-center gap-1.5"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Import Schedule CSV
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Company / Fleet Insured Name</label>
                <input
                  type="text"
                  value={fleetName}
                  onChange={e => setFleetName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Fleet Director / Contact Person</label>
                <input
                  type="text"
                  value={contactPerson}
                  onChange={e => setContactPerson(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Official Telephone</label>
                <input
                  type="text"
                  value={contactPhone}
                  onChange={e => setContactPhone(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Corporate Email Address</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Head Office Depot Location</label>
                <input
                  type="text"
                  value={physicalAddress}
                  onChange={e => setPhysicalAddress(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Past 3-Years Loss Ratio (%) <span className="text-emerald-600 font-normal">(&lt;30% gets +2.5% discount)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={lossRatio}
                  onChange={e => setLossRatio(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Vehicle Schedule Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">2. Scheduled Vehicle Inventory ({vehicles.length} Units)</h2>
                <p className="text-xs text-slate-500">Configure class classification, GPS telematics, and COMESA transit countries</p>
              </div>

              <button
                type="button"
                onClick={handleAddBlankVehicle}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-1.5 shadow-sm"
              >
                <Plus className="w-4 h-4" /> Add Vehicle
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                    <th className="py-3 px-4">#</th>
                    <th className="py-3 px-4">Reg No</th>
                    <th className="py-3 px-4">Make & Model</th>
                    <th className="py-3 px-4">Year</th>
                    <th className="py-3 px-4">Commercial Class</th>
                    <th className="py-3 px-4">Sum Insured (MWK)</th>
                    <th className="py-3 px-4">Capacity / Tonnage</th>
                    <th className="py-3 px-4 text-center">GPS Track</th>
                    <th className="py-3 px-4">COMESA Yellow Card</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {vehicles.map((v, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3 px-4 font-mono text-slate-400">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={v.registrationNumber}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx].registrationNumber = e.target.value;
                            setVehicles(updated);
                          }}
                          className="w-24 font-bold text-slate-900 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900">{v.make} {v.model}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{v.chassisNumber}</div>
                      </td>
                      <td className="py-3 px-4">{v.yearOfManufacture}</td>
                      <td className="py-3 px-4">
                        <select
                          value={v.vehicleClass}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx].vehicleClass = parseInt(e.target.value);
                            setVehicles(updated);
                          }}
                          className="px-2 py-1 bg-white border border-slate-200 rounded text-xs focus:ring-1 focus:ring-indigo-500"
                        >
                          {VEHICLE_CLASSES.map(c => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="number"
                          value={v.sumInsured}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx].sumInsured = parseFloat(e.target.value) || 0;
                            setVehicles(updated);
                          }}
                          className="w-28 text-right font-mono px-2 py-1 bg-white border border-slate-200 rounded text-xs"
                        />
                      </td>
                      <td className="py-3 px-4 text-slate-600">{v.capacityOrTonnage}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...vehicles];
                            updated[idx].hasTrackingSystem = !updated[idx].hasTrackingSystem;
                            setVehicles(updated);
                          }}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            v.hasTrackingSystem
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                        >
                          {v.hasTrackingSystem ? 'YES (-5%)' : 'NO'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <button
                            type="button"
                            onClick={() => handleToggleComesa(idx)}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 ${
                              v.hasComesaYellowCard
                                ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            <Globe className="w-3 h-3" />
                            {v.hasComesaYellowCard ? 'COMESA CERTIFIED' : 'No Yellow Card'}
                          </button>

                          {v.hasComesaYellowCard && (
                            <div className="flex flex-wrap gap-1 max-w-xs mt-1">
                              {COMESA_COUNTRIES.map(country => {
                                const selected = (v.comesaCountries || []).includes(country.id);
                                return (
                                  <button
                                    key={country.id}
                                    type="button"
                                    onClick={() => handleCountryToggle(idx, country.id)}
                                    className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${
                                      selected
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                    }`}
                                  >
                                    {country.code}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveVehicle(idx)}
                          className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Calculate Button Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-xs text-slate-600">
                Total Scheduled: <span className="font-bold text-slate-900">{vehicles.length} units</span> | Total Sum Insured:{' '}
                <span className="font-bold text-slate-900">
                  MWK {vehicles.reduce((sum, v) => sum + (v.sumInsured || 0), 0).toLocaleString()}
                </span>
              </div>

              <button
                type="button"
                onClick={handleCalculateQuote}
                disabled={calculating}
                className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                {calculating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Calculating Rating...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4" /> Calculate Fleet Quotation & Bulk Tiers
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quotation Result Presentation Card */}
          {quoteResult && (
            <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-2xl text-white p-6 shadow-2xl border border-indigo-500/40 space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-4 border-b border-indigo-800/60 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-400 text-slate-950">
                      OFFICIAL QUOTATION
                    </span>
                    <span className="font-mono text-sm text-indigo-300">{quoteResult.quoteNumber}</span>
                  </div>
                  <h3 className="text-xl font-bold">{quoteResult.fleetName}</h3>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs text-indigo-300">Gross Written Premium</div>
                    <div className="text-2xl font-black text-amber-400">
                      MWK {quoteResult.totalGrossWrittenPremium.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  <button
                    onClick={() => setShowBindModal(true)}
                    className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-xl shadow-lg shadow-emerald-500/30 flex items-center gap-2 transition-all"
                  >
                    <CheckCircle className="w-4 h-4" /> Bind Fleet Policy
                  </button>
                </div>
              </div>

              {/* Financial Metrics Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-xs">
                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">Bulk Discount Tier</div>
                  <div className="text-sm font-bold text-emerald-400 mt-1">
                    {String(quoteResult.discountTier)} ({quoteResult.fleetDiscountPercentage}% off)
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">Base Premium</div>
                  <div className="text-sm font-bold text-slate-100 mt-1">
                    MWK {quoteResult.totalBasePremium.toLocaleString()}
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">Fleet Discounts</div>
                  <div className="text-sm font-bold text-emerald-400 mt-1">
                    -MWK {quoteResult.totalFleetDiscounts.toLocaleString()}
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">COMESA Yellow Cards</div>
                  <div className="text-sm font-bold text-amber-400 mt-1">
                    MWK {quoteResult.totalComesaYellowCardPremiums.toLocaleString()} ({quoteResult.yellowCards.length} units)
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">Net Underwriting Prem</div>
                  <div className="text-sm font-bold text-slate-100 mt-1">
                    MWK {quoteResult.totalNetUnderwritingPremium.toLocaleString()}
                  </div>
                </div>

                <div className="bg-slate-800/80 p-3 rounded-xl border border-indigo-900/50">
                  <div className="text-slate-400">VAT (16.5%) & Levies</div>
                  <div className="text-sm font-bold text-slate-100 mt-1">
                    MWK {(quoteResult.totalValueAddedTax + quoteResult.totalStatutoryLevies).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Bound Success Banner if just bound */}
              {boundResult && (
                <div className="p-4 bg-emerald-950/80 border border-emerald-500/60 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-emerald-400 shrink-0" />
                    <div>
                      <div className="font-bold text-emerald-300">Policy Successfully Bound In-Force!</div>
                      <div className="text-xs text-slate-300 font-mono">Policy Number: {boundResult.policyNumber}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => coreInsuranceApi.downloadFleetSchedulePdf(boundResult.policyNumber)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Fleet Schedule PDF
                    </button>

                    {boundResult.yellowCards.length > 0 && (
                      <button
                        onClick={() => coreInsuranceApi.downloadComesaYellowCardPdf(boundResult.yellowCards[0].certificateNumber)}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow transition-colors"
                      >
                        <Globe className="w-3.5 h-3.5" /> Yellow Card PDF ({boundResult.yellowCards[0].certificateNumber})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: BOUND FLEETS DIRECTORY */}
      {activeTab === 'directory' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-4">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Bound Commercial Fleet Policies</h2>
              <p className="text-xs text-slate-500">Active commercial lines policies underwritten and in-force</p>
            </div>

            <button
              onClick={fetchBoundFleets}
              className="p-2 text-slate-600 hover:text-indigo-600 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6">
            {boundFleets.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No commercial fleet policies bound yet in this session.</p>
                <button
                  onClick={() => setActiveTab('quote')}
                  className="mt-3 px-4 py-2 text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-lg"
                >
                  Create New Fleet Quotation
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                      <th className="py-3 px-4">Policy No</th>
                      <th className="py-3 px-4">Fleet Insured</th>
                      <th className="py-3 px-4">Units</th>
                      <th className="py-3 px-4">Total Sum Insured</th>
                      <th className="py-3 px-4">Gross Written Premium</th>
                      <th className="py-3 px-4">Payment Method</th>
                      <th className="py-3 px-4">COMESA Yellow Cards</th>
                      <th className="py-3 px-4 text-right">Documents</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {boundFleets.map(f => (
                      <tr key={f.policyNumber} className="hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono font-bold text-indigo-600">{f.policyNumber}</td>
                        <td className="py-3 px-4 font-semibold text-slate-900">{f.fleetName}</td>
                        <td className="py-3 px-4">{f.totalVehicles} vehicles</td>
                        <td className="py-3 px-4 font-mono">MWK {f.totalSumInsured.toLocaleString()}</td>
                        <td className="py-3 px-4 font-mono font-bold text-slate-900">
                          MWK {f.grossWrittenPremium.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                            {f.paymentMethod}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                            {f.yellowCards.length} certificates
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => coreInsuranceApi.downloadFleetSchedulePdf(f.policyNumber)}
                              className="px-2.5 py-1 text-[11px] font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded flex items-center gap-1 transition-colors"
                            >
                              <Download className="w-3 h-3" /> Schedule
                            </button>

                            {f.yellowCards.length > 0 && (
                              <button
                                onClick={() => coreInsuranceApi.downloadComesaYellowCardPdf(f.yellowCards[0].certificateNumber)}
                                className="px-2.5 py-1 text-[11px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 rounded flex items-center gap-1 transition-colors"
                              >
                                <Globe className="w-3 h-3" /> Yellow Card
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: COMESA HUB */}
      {activeTab === 'comesa' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
              Cross-Border Third-Party Scheme
            </span>
            <h2 className="text-xl font-bold text-slate-900 mt-2">
              COMESA Yellow Card Scheme (Carte Jaune COMESA)
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              The Protocol on the COMESA Third-Party Motor Vehicle Insurance Scheme guarantees cross-border transit coverage.
              A single digital Yellow Card issued by Xtrem General Insurance provides legal third-party liability cover
              across all designated transit corridors without requiring drivers to buy local insurance at every border post.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {COMESA_COUNTRIES.map(c => (
              <div key={c.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs">
                  {c.code}
                </div>
                <div>
                  <div className="font-bold text-slate-900 text-xs">{c.name}</div>
                  <div className="text-[10px] text-slate-500">Bureau Connected</div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-3 text-xs text-amber-900">
            <Shield className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold mb-1">National Bureau of Malawi Interconnection</div>
              <p>
                All certificates generated by the Xtrem Insurance PAS are cryptographically signed with SHA-256 verification hashes,
                enabling roadside traffic police and customs border checkpoints across SADC and COMESA to verify authenticity via the
                public roadside inspection gateway.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* BIND MODAL */}
      {showBindModal && quoteResult && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Bind Commercial Fleet Policy</h3>
            <p className="text-xs text-slate-500">
              Confirm payment receipt and issue official policy schedule with {quoteResult.totalVehiclesCount} scheduled vehicles.
            </p>

            <div className="p-4 bg-slate-50 rounded-xl space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Fleet Account:</span>
                <span className="font-bold text-slate-900">{quoteResult.fleetName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Total Vehicles:</span>
                <span className="font-bold text-slate-900">{quoteResult.totalVehiclesCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">COMESA Yellow Cards:</span>
                <span className="font-bold text-amber-600">{quoteResult.yellowCards.length} certificates</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="font-bold text-slate-900">Total Amount Due:</span>
                <span className="font-bold text-indigo-600 text-sm">
                  MWK {quoteResult.totalGrossWrittenPremium.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="BankTransfer">Direct Bank Transfer (EFT)</option>
                  <option value="AirtelMoney">Airtel Money Push</option>
                  <option value="TnmMpamba">TNM Mpamba Push</option>
                  <option value="Cheque">Corporate Cheque</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bank Reference / Remittance Code</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3">
              <button
                type="button"
                onClick={() => setShowBindModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBindFleet}
                disabled={binding}
                className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow transition-colors flex items-center gap-1.5"
              >
                {binding ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Confirm & Bind Policy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: IOT TELEMATICS & DRIVER BEHAVIOR RISK SCORING */}
      {activeTab === 'telematics' && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Shield className="text-emerald-600" />
                IoT Sensor Telematics & Driver Behavior Analytics
              </h2>
              <p className="text-xs text-slate-500">
                Real-time OBD-II/GPS telemetry analysis for <span className="font-semibold">{fleetName}</span>. Computes harsh acceleration, braking, night driving, and fatigue risk scoring.
              </p>
            </div>
            <button
              onClick={async () => {
                setLoadingTelematics(true);
                try {
                  const regs = vehicles.map(v => v.registrationNumber);
                  const data = await coreInsuranceApi.getFleetTelematicsAudit(fleetName, regs);
                  setTelematicsReport(data);
                  toast.success('Telemetry audit stream refreshed!');
                } catch {
                  toast.error('Failed to refresh telematics audit');
                } finally {
                  setLoadingTelematics(false);
                }
              }}
              disabled={loadingTelematics}
              className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow transition-colors flex items-center gap-2"
            >
              {loadingTelematics ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Re-run Fleet Sensor Audit
            </button>
          </div>

          {loadingTelematics ? (
            <div className="p-12 text-center text-slate-400 bg-white rounded-2xl border">
              <RefreshCw className="animate-spin inline mr-2" size={20} />
              Evaluating sensor telemetry streams across all fleet vehicles...
            </div>
          ) : telematicsReport ? (
            <div className="space-y-6">
              {/* Telematics Summary Banner */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Average Fleet Score</div>
                  <div className="text-3xl font-extrabold text-slate-900 mt-1">
                    {telematicsReport.averageFleetScore} <span className="text-xs font-normal text-slate-400">/ 100</span>
                  </div>
                  <div className="text-xs text-emerald-600 font-medium mt-1">Tier: Exemplary Safe Driving</div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Monitored Units</div>
                  <div className="text-3xl font-extrabold text-slate-900 mt-1">
                    {telematicsReport.totalMonitoredVehicles}
                  </div>
                  <div className="text-xs text-indigo-600 font-medium mt-1">100% active GPS/telematics links</div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Harsh Incidents (30d)</div>
                  <div className="text-3xl font-extrabold text-amber-600 mt-1">
                    {telematicsReport.totalHarshEventsRecorded}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">Braking, cornering & speeding</div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-slate-500 uppercase font-semibold">Premium Adjustment</div>
                  <div className="text-3xl font-extrabold text-emerald-600 mt-1">
                    {(telematicsReport.aggregateFleetAdjustmentPercent * 100).toFixed(1)}%
                  </div>
                  <div className="text-xs text-emerald-700 font-medium mt-1">Underwriting discount rebate applied</div>
                </div>
              </div>

              {/* Per-Vehicle Telematics Inspector Table */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 font-bold text-sm text-slate-900">
                  Vehicle-by-Vehicle Behavior Matrix & Safety Recommendations
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600 font-semibold border-b">
                        <th className="py-3 px-4">Registration</th>
                        <th className="py-3 px-4">Score (0-100)</th>
                        <th className="py-3 px-4">Harsh Braking</th>
                        <th className="py-3 px-4">Harsh Accel</th>
                        <th className="py-3 px-4">Speeding Incidents</th>
                        <th className="py-3 px-4">Night Driving (Hrs)</th>
                        <th className="py-3 px-4">Fatigue Warnings</th>
                        <th className="py-3 px-4">Premium Adjustment</th>
                        <th className="py-3 px-4">Audit Recommendation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {telematicsReport.vehicleSummaries?.map((v: any, i: number) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="py-3 px-4 font-bold font-mono text-slate-900">{v.vehicleRegistration}</td>
                          <td className="py-3 px-4 font-extrabold">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              v.overallScore >= 85 ? 'bg-emerald-100 text-emerald-800' :
                              v.overallScore >= 70 ? 'bg-blue-100 text-blue-800' :
                              v.overallScore >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {v.overallScore}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-700">{v.harshBrakingEvents}</td>
                          <td className="py-3 px-4 text-slate-700">{v.harshAccelerationEvents}</td>
                          <td className="py-3 px-4 text-slate-700">{v.speedingIncidents}</td>
                          <td className="py-3 px-4 text-slate-700">{v.nightDrivingHours}h</td>
                          <td className="py-3 px-4">
                            {v.driverFatigueWarnings > 0 ? (
                              <span className="text-red-600 font-bold">{v.driverFatigueWarnings} alerts</span>
                            ) : (
                              <span className="text-emerald-600 font-medium">0 alerts</span>
                            )}
                          </td>
                          <td className="py-3 px-4 font-mono font-bold text-emerald-600">
                            {(v.premiumAdjustmentPercent * 100).toFixed(1)}%
                          </td>
                          <td className="py-3 px-4 text-[11px] text-slate-600 max-w-xs">{v.safetyRecommendations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500 bg-white rounded-2xl border">
              Click "Re-run Fleet Sensor Audit" to query real-time vehicle telematics.
            </div>
          )}
        </div>
      )}

      {/* CSV IMPORT MODAL */}
      {showCsvModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-200 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Bulk Vehicle Schedule CSV Importer</h3>
            <p className="text-xs text-slate-500">
              Paste vehicle data below. Format: <br />
              <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px] font-mono">
                Reg, Make, Model, Year, Chassis, Class(1-6), SumInsured, Tonnage, Tracking(true/false), Comesa(true/false)
              </code>
            </p>

            <textarea
              rows={8}
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={`Registration,Make,Model,Year,Chassis,Class,SumInsured,Tonnage,Tracking,Comesa\nBZ 1001,Scania,R500,2021,YS2R4X,3,45000000,34 Tonnes,true,true\nBZ 2001,Mercedes,Actros Tanker,2021,WDB934,5,52000000,45000 Litres,true,true`}
              className="w-full p-3 font-mono text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCsvModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCsvImport}
                className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition-colors flex items-center gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" /> Parse & Import Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
